/**
 * Batch Location Creation - Generated Location Models
 * 
 * Modelos para representar ubicaciones generadas en el proceso
 * de creación en lote antes de ser persistidas en el backend.
 * 
 * @module GeneratedLocation
 */

import { LocationDepth } from './batch-creation-config.model';

/**
 * Tipos de ubicación según el nivel jerárquico
 */
export type LocationType = 
  | 'SALON'           // Nivel 0 (padre existente)
  | 'ROW'             // Nivel 1 en profundidad >= 2
  | 'RACK-FRONT'      // Nivel 2 en profundidad >= 3
  | 'LEVEL-FRONT'     // Nivel 3 en profundidad = 4
  | 'POSITION-FRONT'; // Último nivel (siempre)

/**
 * Índices de posición en la estructura jerárquica
 * Representa la ubicación de un elemento en cada nivel
 */
export interface LocationIndices {
  /**
   * Índice de fila (1-based)
   * Presente en profundidad >= 2
   */
  rowIndex?: number;

  /**
   * Índice de rack (1-based)
   * Presente en profundidad >= 3
   */
  rackIndex?: number;

  /**
   * Índice de nivel (1-based)
   * Presente en profundidad = 4
   */
  levelIndex?: number;

  /**
   * Índice de posición (1-based)
   * Presente solo en ubicaciones POSITION-FRONT
   */
  positionIndex?: number;
}

/**
 * Ubicación generada durante el proceso de batch creation
 * Representa una ubicación antes de ser persistida en el backend
 */
export interface GeneratedLocation {
  /**
   * Nombre único de la ubicación (nomenclatura completa)
   * Ejemplo: "S1-F5-R4-L3-P10"
   */
  name: string;

  /**
   * Nombre para mostrar (igual que name por defecto)
   */
  display_name: string;

  /**
   * Tipo de ubicación según el nivel
   */
  type: LocationType;

  /**
   * ID del tipo de ubicación en el sistema
   * Se obtiene del catálogo de tipos de ubicación
   */
  type_id?: number;

  /**
   * ID del padre inmediato en la jerarquía
   * - Para ROW: ID del SALON
   * - Para RACK: ID del ROW
   * - Para LEVEL: ID del RACK
   * - Para POSITION: ID del nivel anterior (ROW, RACK, LEVEL o SALON)
   */
  parent_id?: number;

  /**
   * Nombre del padre inmediato (para preview)
   */
  parent_name?: string;

  /**
   * Índices de posición en la estructura
   */
  indices: LocationIndices;

  /**
   * Profundidad en la estructura (nivel del árbol)
   * - 1: ROW o POSITION directa
   * - 2: RACK o POSITION bajo ROW
   * - 3: LEVEL o POSITION bajo RACK
   * - 4: POSITION bajo LEVEL
   */
  level: number;

  /**
   * Ruta completa de nombres desde la raíz
   * Ejemplo: ["SALON-1", "F5", "R4", "L3", "P10"]
   */
  path: string[];

  /**
   * ID del customer propietario
   */
  customer_id?: number;

  /**
   * Descripción (opcional)
   */
  description?: string;

  /**
   * External ID (opcional)
   */
  external_id?: string;

  /**
   * Propiedades adicionales (opcional)
   */
  properties?: Record<string, any>;

  /**
   * Parámetros adicionales (opcional)
   */
  params?: Record<string, any>;
}

/**
 * Resultado de la generación de ubicaciones
 * Contiene todas las ubicaciones generadas organizadas por tipo
 */
export interface GeneratedLocationsResult {
  /**
   * Lista plana de todas las ubicaciones generadas
   * Ordenadas por nivel jerárquico (padres primero)
   */
  locations: GeneratedLocation[];

  /**
   * Ubicaciones agrupadas por tipo
   */
  byType: {
    rows: GeneratedLocation[];
    racks: GeneratedLocation[];
    levels: GeneratedLocation[];
    positions: GeneratedLocation[];
  };

  /**
   * Estadísticas de generación
   */
  statistics: {
    /**
     * Total de ubicaciones generadas
     */
    total: number;

    /**
     * Cantidad por tipo
     */
    byType: {
      rows: number;
      racks: number;
      levels: number;
      positions: number;
    };

    /**
     * Profundidad de la estructura
     */
    depth: LocationDepth;

    /**
     * Estimación de tiempo de creación (segundos)
     * Basado en 100 ubicaciones/segundo en backend
     */
    estimatedCreationTime: number;
  };

  /**
   * Preview de nomenclaturas (para mostrar al usuario)
   */
  preview: {
    /**
     * Primera ubicación de cada tipo
     */
    first: {
      row?: string;
      rack?: string;
      level?: string;
      position: string;
    };

    /**
     * Última ubicación de cada tipo
     */
    last: {
      row?: string;
      rack?: string;
      level?: string;
      position: string;
    };

    /**
     * Algunas ubicaciones intermedias (máximo 3 de cada tipo)
     */
    sample: {
      rows: string[];
      racks: string[];
      levels: string[];
      positions: string[];
    };
  };
}

/**
 * Mapeo de LocationDepth a LocationType para el último nivel
 */
export const DEPTH_TO_POSITION_PARENT: Record<LocationDepth, LocationType> = {
  1: 'SALON',       // Posiciones directas bajo salón
  2: 'ROW',         // Posiciones bajo fila
  3: 'RACK-FRONT',  // Posiciones bajo rack
  4: 'LEVEL-FRONT', // Posiciones bajo nivel
};

/**
 * Orden de los tipos de ubicación en la jerarquía
 * Usado para ordenar ubicaciones antes de persistir (padres primero)
 */
export const LOCATION_TYPE_ORDER: Record<LocationType, number> = {
  'SALON': 0,
  'ROW': 1,
  'RACK-FRONT': 2,
  'LEVEL-FRONT': 3,
  'POSITION-FRONT': 4,
};

/**
 * Obtiene el tipo de ubicación según el nivel y la profundidad
 * 
 * @param level - Nivel en la jerarquía (1-based)
 * @param depth - Profundidad total de la estructura
 * @returns Tipo de ubicación correspondiente
 */
export function getLocationType(level: number, depth: LocationDepth): LocationType {
  // El último nivel siempre es POSITION-FRONT
  if (level === depth) {
    return 'POSITION-FRONT';
  }

  // Niveles intermedios dependen de la profundidad
  switch (depth) {
    case 1:
      return 'POSITION-FRONT'; // Solo posiciones

    case 2:
      if (level === 1) return 'ROW';
      return 'POSITION-FRONT';

    case 3:
      if (level === 1) return 'ROW';
      if (level === 2) return 'RACK-FRONT';
      return 'POSITION-FRONT';

    case 4:
      if (level === 1) return 'ROW';
      if (level === 2) return 'RACK-FRONT';
      if (level === 3) return 'LEVEL-FRONT';
      return 'POSITION-FRONT';

    default:
      throw new Error(`Profundidad inválida: ${depth}`);
  }
}

/**
 * Ordena ubicaciones generadas por nivel jerárquico
 * Padres primero, hijos después
 * 
 * @param locations - Array de ubicaciones a ordenar
 * @returns Array ordenado (no modifica el original)
 */
export function sortLocationsByHierarchy(
  locations: GeneratedLocation[]
): GeneratedLocation[] {
  return [...locations].sort((a, b) => {
    // Primero por nivel (padres primero)
    if (a.level !== b.level) {
      return a.level - b.level;
    }

    // Luego por tipo (orden jerárquico)
    const orderA = LOCATION_TYPE_ORDER[a.type];
    const orderB = LOCATION_TYPE_ORDER[b.type];
    if (orderA !== orderB) {
      return orderA - orderB;
    }

    // Finalmente por nombre (orden natural con números)
    // Usamos numeric: true para ordenar correctamente P1, P2, ..., P9, P10
    return a.name.localeCompare(b.name, undefined, { numeric: true });
  });
}

/**
 * Agrupa ubicaciones por tipo
 * 
 * @param locations - Array de ubicaciones
 * @returns Ubicaciones agrupadas por tipo
 */
export function groupLocationsByType(locations: GeneratedLocation[]): {
  rows: GeneratedLocation[];
  racks: GeneratedLocation[];
  levels: GeneratedLocation[];
  positions: GeneratedLocation[];
} {
  return {
    rows: locations.filter(loc => loc.type === 'ROW'),
    racks: locations.filter(loc => loc.type === 'RACK-FRONT'),
    levels: locations.filter(loc => loc.type === 'LEVEL-FRONT'),
    positions: locations.filter(loc => loc.type === 'POSITION-FRONT'),
  };
}

/**
 * Calcula tiempo estimado de creación (en segundos)
 * Basado en benchmark de 100 ubicaciones/segundo
 * 
 * @param totalLocations - Total de ubicaciones a crear
 * @returns Tiempo estimado en segundos
 */
export function estimateCreationTime(totalLocations: number): number {
  const LOCATIONS_PER_SECOND = 100;
  return Math.ceil(totalLocations / LOCATIONS_PER_SECOND);
}
